import React, { useEffect, useState } from "react";
import { getCandlestickData } from "../../utils/api";
import { SkeletonBlock } from "../Skeleton/Skeleton";

const W = 72;
const H = 28;

// Mini price chart for a holding, using the same candlestick source as /charts
// at the default 1-hour timeframe. Shows a small loader in the chart space
// until the data resolves; renders nothing if data is unavailable.
const HoldingSparkline = ({ symbol }) => {
  const [closes, setCloses] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(false);
    (async () => {
      try {
        // getCandlestickData(symbol, startDate, period, multiplier, endDate, limit)
        const data = await getCandlestickData(
          symbol,
          undefined,
          "hour",
          undefined,
          undefined,
          60
        );
        let arr = [];
        if (Array.isArray(data)) {
          arr = data
            .map((b) => Number(b.close ?? b.c ?? b.Close))
            .filter((v) => !Number.isNaN(v));
        } else if (data && Array.isArray(data.Close)) {
          arr = data.Close.map(Number).filter((v) => !Number.isNaN(v));
        }
        if (!cancelled) {
          setCloses(arr.slice(-60));
          setLoading(false);
        }
      } catch (e) {
        console.error("sparkline fetch failed for", symbol, e);
        if (!cancelled) {
          setError(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  if (loading) {
    // A sparkline-sized grey rectangle. The old spinner was a green spinning
    // ring in a cell whose real content is a green/red performance line — it
    // read as a direction before any data existed.
    return (
      <div className="flex-shrink-0" style={{ width: W, height: H }}>
        <SkeletonBlock width={W} height={H} rounded="rounded" />
      </div>
    );
  }

  if (error || !closes || closes.length < 2) {
    return <div style={{ width: W, height: H }} className="flex-shrink-0" />;
  }

  const min = Math.min(...closes);
  const max = Math.max(...closes);
  const range = max - min || 1;
  const stepX = W / (closes.length - 1);
  const points = closes
    .map(
      (p, i) =>
        `${(i * stepX).toFixed(1)},${(H - ((p - min) / range) * H).toFixed(1)}`
    )
    .join(" ");
  // Always green for now (client may want red for down moves later).
  const color = "#16A34A";

  return (
    <svg
      width={W}
      height={H}
      viewBox={`0 0 ${W} ${H}`}
      className="flex-shrink-0"
      aria-hidden="true"
    >
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
};

export default HoldingSparkline;
